import React, { Suspense, lazy, useCallback, useRef, useState } from "react";
import { Smile } from "lucide-react";
import AnchoredPopover from "./AnchoredPopover";
import { useResponsive } from "./responsive";
import { useTranslation } from "../i18n";
import { getCurrentLocale } from "../utils/locale";

// Full emoji keyboard (emoji-mart). The picker, its emoji data and its locale
// file are all code-split so none of it lands in the main bundle until someone
// opens the keyboard.
//
// Emoji are inserted as plain Unicode text, never as images or shortcodes, so
// a message reads the same everywhere it travels: the feed, Amazon Translate,
// notifications and Workvivo posts/kudos. `set="native"` keeps the picker
// showing the device's own glyphs, i.e. exactly what Workvivo will render.
const Picker = lazy(() => import("@emoji-mart/react"));

const loadData = () => import("@emoji-mart/data").then((m) => m.default || m);

// Locales emoji-mart ships translations for; everything else stays English.
// emoji-mart uses "kr" for Korean.
const PICKER_LOCALES = new Set([
  "ar", "be", "cs", "de", "en", "es", "fa", "fi", "fr", "hi", "it", "ja",
  "kr", "nl", "pl", "pt", "ru", "sa", "tr", "uk", "vi", "zh",
]);

const pickerLocale = () => {
  const base = (getCurrentLocale() || "en").split(/[-_]/)[0].toLowerCase();
  const code = base === "ko" ? "kr" : base;
  return PICKER_LOCALES.has(code) ? code : "en";
};

const loadI18n = (code) =>
  import(`@emoji-mart/data/i18n/${code}.json`)
    .then((m) => m.default || m)
    .catch(() => import("@emoji-mart/data/i18n/en.json").then((m) => m.default || m));

/**
 * The emoji keyboard in a popover / bottom sheet. Stays open after a pick so
 * several emoji can be added in a row.
 */
const EmojiKeyboard = ({ anchorRef, onSelect, onClose }) => {
  const { t } = useTranslation("common");
  const { isMobile } = useResponsive();
  const [locale] = useState(pickerLocale);

  return (
    <AnchoredPopover anchorRef={anchorRef} onClose={onClose} title={t("emoji.title")}>
      <Suspense
        fallback={
          <p className="text-sm text-gray-500 text-center py-8 px-16 bg-white">
            {t("emoji.loading")}
          </p>
        }
      >
        <Picker
          data={loadData}
          i18n={() => loadI18n(locale)}
          locale={locale}
          set="native"
          theme="light"
          autoFocus={!isMobile}
          dynamicWidth={isMobile}
          previewPosition="none"
          skinTonePosition="search"
          maxFrequentRows={2}
          onEmojiSelect={(emoji) => {
            if (emoji?.native) onSelect(emoji.native);
          }}
        />
      </Suspense>
    </AnchoredPopover>
  );
};

/**
 * Toolbar button that opens the emoji keyboard and inserts the chosen emoji
 * at the cursor of a TipTap editor. Pass `renderButton` to reuse a toolbar's
 * own button component; otherwise a plain icon button is rendered.
 */
export const EmojiToolbarButton = ({ editor, className = "", renderButton }) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const insert = (native) => {
    // Insert as a text node so the emoji is never parsed as HTML.
    editor?.chain().focus().insertContent({ type: "text", text: native }).run();
  };

  const toggle = () => setOpen((v) => !v);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      {renderButton ? (
        renderButton({ ref: btnRef, onClick: toggle, label: t("emoji.add"), active: open })
      ) : (
        <button
          type="button"
          ref={btnRef}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggle}
          title={t("emoji.add")}
          aria-label={t("emoji.add")}
          aria-expanded={open}
          className={`px-2 py-1 rounded text-gray-600 ${open ? "bg-gray-200" : "hover:bg-gray-100"} ${className}`}
        >
          <Smile className="w-4 h-4" />
        </button>
      )}
      {open && <EmojiKeyboard anchorRef={btnRef} onSelect={insert} onClose={close} />}
    </>
  );
};

export default EmojiKeyboard;
